import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import _, { cloneDeep } from "lodash";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { AutoComplete } from "primereact/autocomplete";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { Utility } from "../../core/Utility";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { RoleType } from "../../models/Role";
import { Disposition } from "../../models/Disposition";
import { DocumentOfReceive } from "../../models/DocumentOfReceive";
import { activeUser } from "../../services/AuthService";
import { sendDispositionEmails } from "../../services/DispositionEmailService";
import { getAllUsers } from "../../services/UserService";
import { getAllPartners } from "../../services/PartnerService";
import {
  getAllDispositionsByDate,
  getAllDispositionsProductInputByDate,
} from "../../services/NewVersionService";
import "./DispositionsMapPageNew.scss";

export const DispositionsMapPageNew = () => {
  const { t } = useTranslation();

  const [emailAddress, setEmailAddress] = useState<string[]>([]);
  const [allDispositions, setAllDispositions] = useState<Disposition[]>([]);
  const [allProductInputs, setAllProductInputs] = useState<DocumentOfReceive[]>(
    [],
  );
  const [allSelectedDispositions, setAllSelectedDispositions] = useState<
    Disposition[]
  >([]);
  const [partnersLocal, setPartnersLocal] = useState<any[]>([]);
  const [allEmails, setAllEmails] = useState<string[]>([]);
  const [emailsSugestions, setEmailsSugestions] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<Date>(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 5,
      0,
      0,
      1,
    ),
  ); //5 days ago 00:00:01h
  const [processing, setProcessing] = useState(false);
  const [filters, setFilters] = useState({
    documentNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    price: { value: null, matchMode: FilterMatchMode.CONTAINS },
    registrationNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    loadingFinished: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  useEffect(() => {
    setProcessing(true);
    Promise.all([
      getAllDispositionsByDate(
        new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate() - 5,
          0,
          0,
          1,
        ),
      ),
      getAllDispositionsProductInputByDate(
        new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate() - 5,
          0,
          0,
          1,
        ),
      ),
      getAllUsers(),
      getAllPartners(),
    ])
      .then((response) => {
        const allNewDispositions = response[0].data.result;
        const sortedDispositionsByDate = allNewDispositions.sort(
          (a: Disposition, b: Disposition) =>
            new Date(a?.dateFrom as Date).getTime() <
            new Date(b?.dateFrom as Date).getTime()
              ? 1
              : -1,
        );
        setAllDispositions(sortedDispositionsByDate);
        const allNewDispositionsProductInput = response[1].data.result;
        setAllProductInputs(allNewDispositionsProductInput);

        //set email sugestions
        const usersResp = response[2]?.data?.result ?? response[2]?.data ?? [];
        const partnersResp =
          response[3]?.data?.result ?? response[3]?.data ?? [];

        setPartnersLocal(partnersResp);

        const emails: string[] = [];
        usersResp.forEach((u: any) => {
          if (u?.email) emails.push(u.email);
        });
        partnersResp.forEach((p: any) => {
          if (p?.email) emails.push(p.email);
        });
        setAllEmails(emails);
      })
      .finally(() => setProcessing(false));
  }, []);

  useEffect(() => {
    setProcessing(true);
    Promise.all([
      getAllDispositionsByDate(new Date(dateFrom)),
      getAllDispositionsProductInputByDate(new Date(dateFrom)),
    ])
      .then((response) => {
        const allNewDispositions = response[0].data.result;
        const sortedDispositionsByDate = allNewDispositions.sort(
          (a: Disposition, b: Disposition) =>
            new Date(a?.dateFrom as Date).getTime() <
            new Date(b?.dateFrom as Date).getTime()
              ? 1
              : -1,
        );
        setAllDispositions(sortedDispositionsByDate);
        const allNewDispositionsProductInput = response[1].data.result;
        setAllProductInputs(allNewDispositionsProductInput);
      })
      .finally(() => setProcessing(false));
  }, [dateFrom]);

  const toast = useRef(null);

  const filterEmails = function (e: any) {
    let results = allEmails.filter((email) => {
      return email.toLowerCase().startsWith(e.query.toLowerCase());
    });
    setEmailsSugestions(results);
  };

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  const onDateFromChange = (property: string, value: Date | null) => {
    setDateFrom(value as Date);
  };

  const formatDocumentNumber = (rowData: Disposition) => {
    let documentIdToShow = "";
    let isDocumentFinished: boolean = false;
    const dispositionId = rowData.id;
    allProductInputs.map((item) => {
      if (item.dispositionId === dispositionId) {
        documentIdToShow = item.finishedDocumentId ?? ("" as string);
        isDocumentFinished = item.finishedDocument as boolean;
      }
    });
    return (
      <span className={isDocumentFinished ? "green-text" : "red-text"}>
        {documentIdToShow}
      </span>
    );
  };

  const formatLoadingFinished = (rowData: Disposition) => {
    return <span>{rowData.loadingFinished === true ? "Da" : "Ne"}</span>;
  };

  const loadingFinishedOptions = [
    { label: t("Da"), value: true },
    { label: t("Ne"), value: false },
  ];

  const loadingFinishedItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const loadingFinishedRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={loadingFinishedOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={loadingFinishedItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const formatDateFrom = (rowData: Disposition) => {
    return Utility.formatDate(new Date(rowData?.dateFrom as Date));
  };

  const formatTransportationPartner = (rowData: Disposition) => {
    return (
      partnersLocal.find(
        (item) => rowData?.transportationPartner?.id == item.id,
      )?.name ?? ""
    );
  };

  const formatVehicleType = (rowData: Disposition) => {
    return (
      <div>
        {rowData.vehicleTypeId == "1"
          ? "Kamion"
          : rowData.vehicleTypeId == "2"
            ? "Kamion sa prikolicom"
            : rowData.vehicleTypeId == "3"
              ? "Kombi"
              : rowData.vehicleTypeId == "4"
                ? "Traktor"
                : rowData.vehicleTypeId == "5"
                  ? "Vagon"
                  : rowData.vehicleTypeId == "6"
                    ? "Automobil"
                    : ""}
      </div>
    );
  };

  const formatSendEmail = (rowData: Disposition) => {
    return (
      <div className="action-button">
        <Checkbox
          id="emailSelected"
          checked={allSelectedDispositions.some(
            (item) => item.id === rowData.id,
          )}
          onChange={(e) => {
            const id = rowData.id;
            const selectedDisposition = allDispositions.find(
              (disposition) => disposition.id === id,
            );
            let copyOfAllSelectedDispositions: Disposition[] = [];
            if (e.checked === true) {
              copyOfAllSelectedDispositions = cloneDeep(
                allSelectedDispositions,
              );
              copyOfAllSelectedDispositions.push(
                selectedDisposition as Disposition,
              );
            } else {
              copyOfAllSelectedDispositions = allSelectedDispositions.filter(
                (item) => item.id !== id,
              );
            }
            setAllSelectedDispositions(copyOfAllSelectedDispositions);
          }}
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <div className="map-wrapper">
        <Toast ref={toast} position={"top-right"} />
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-truck header-title-icon"
              style={{
                fontSize: 16,
                marginLeft: "1rem",
                marginRight: "1rem",
                transform: "translate(0, 5px)",
              }}
            ></i>
            <span className="header-left-title">{t("Sve dispozicije")}</span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <AutoComplete
              id="emailAddress"
              value={emailAddress}
              suggestions={emailsSugestions}
              multiple={true}
              completeMethod={filterEmails}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder={t("Upišite e-mailove") as string}
              className={"email-input-field"}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 1rem 0 2rem",
              }}
              labelText={t("Pošalji")}
              icon="bi bi-envelope"
              onButtonClick={() => {
                //format multiple email
                let multipleEmail: string = "";
                emailAddress?.map((oneEmail: any) => {
                  multipleEmail += oneEmail + ";";
                });
                if (multipleEmail.length > 0) {
                  multipleEmail = multipleEmail.slice(0, -1);
                }
                if (
                  multipleEmail !== "" &&
                  allSelectedDispositions.length > 0
                ) {
                  sendDispositionEmails(multipleEmail, allSelectedDispositions); //@ts-ignore
                  toast.current.show({
                    severity: "success",
                    detail: t(
                      `Uspešno poslati podaci o selektovanim dispozicijama na e-mailove`,
                    ),
                    life: 3000,
                  });
                  setEmailAddress([]);
                  setAllSelectedDispositions([]);
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Za slanje e-mailova, neophodno je selektovati bar jednu dispoziciju i uneti bar jednu email adresu`,
                    ),
                    life: 3000,
                  });
                }
              }}
              disabled={activeUser?.role?.name === RoleType.Director}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              emptyMessage="Lista dispozicija je prazna"
              value={allDispositions}
              showGridlines
              stripedRows
            >
              <Column
                body={formatDocumentNumber}
                header={t("Br.Dok.")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatLoadingFinished}
                filter
                filterField={"loadingFinished"}
                showFilterMenu={false}
                filterElement={loadingFinishedRowFilterTemplate}
                header={t("Zaključena dispozicija")}
                style={{ minWidth: "50px" }}
              />
              <Column
                body={formatDateFrom}
                field="dateFrom"
                sortable
                header={t("Datum")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="dispositionNumberFormatted"
                sortable
                filter
                header={t("Br.disp.")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatTransportationPartner}
                field="transportationPartner"
                sortable
                header={t("Prevoznik")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatVehicleType}
                header={t("Tip vozila")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="registrationNumber"
                filter
                header={t("Br.registr.")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="driverName"
                filter
                header={t("Vozač")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="price"
                filter
                header={t("Cena prevoza")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatSendEmail}
                header={t("Poslati mail")}
                style={{ minWidth: "100px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
